import { Clock, CheckCircle2, XCircle, AlertCircle, MoreHorizontal, Edit, Copy, Trash2, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn, formatRelativeTime, formatCronExpression } from '@/lib/utils';

interface PipelineListProps {
  pipelines: Array<{
    id: string;
    name: string;
    description?: string;
    lastExecution?: {
      status: 'success' | 'error' | 'running' | 'pending';
      startedAt: string;
      duration?: number;
    };
    nodeCount: number;
    healthStatus: 'healthy' | 'warning' | 'error';
    schedule?: string;
    nextRunAt?: string;
  }>;
  onRun: (id: string) => void;
  onEdit: (id: string) => void;
  onSchedule: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  sortBy: 'lastRun' | 'name' | 'created';
  onSortChange: (sort: 'lastRun' | 'name' | 'created') => void;
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'error':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'running':
      return <Clock className="h-4 w-4 text-blue-500 animate-pulse" />;
    case 'pending':
      return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    default:
      return null;
  }
}

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'success':
      return 'outline';
    case 'error':
      return 'destructive';
    case 'running':
      return 'default';
    case 'pending':
      return 'secondary';
    default:
      return 'outline';
  }
}

function SortIndicator({ column, currentSort }: { column: string; currentSort: string }) {
  if (currentSort !== column) return null;
  return <span className="ml-1">↓</span>;
}

export function PipelineList({
  pipelines,
  onRun,
  onEdit,
  onSchedule,
  onDuplicate,
  onDelete,
  sortBy,
  onSortChange,
}: PipelineListProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[250px]">
              <button
                onClick={() => onSortChange('name')}
                className="flex items-center font-medium hover:text-foreground transition-colors"
              >
                Name <SortIndicator column="name" currentSort={sortBy} />
              </button>
            </TableHead>
            <TableHead>
              <button
                onClick={() => onSortChange('lastRun')}
                className="flex items-center font-medium hover:text-foreground transition-colors"
              >
                Status <SortIndicator column="lastRun" currentSort={sortBy} />
              </button>
            </TableHead>
            <TableHead>
              <button
                onClick={() => onSortChange('lastRun')}
                className="flex items-center font-medium hover:text-foreground transition-colors"
              >
                Last Run <SortIndicator column="lastRun" currentSort={sortBy} />
              </button>
            </TableHead>
            <TableHead>Schedule</TableHead>
            <TableHead>Nodes</TableHead>
            <TableHead>Health</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pipelines.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No pipelines found
              </TableCell>
            </TableRow>
          ) : (
            pipelines.map((pipeline) => (
              <TableRow key={pipeline.id}>
                <TableCell className="font-medium">
                  <div>
                    <div className="font-medium">{pipeline.name}</div>
                    {pipeline.description && (
                      <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                        {pipeline.description}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {pipeline.lastExecution ? (
                    <div className="flex items-center gap-2">
                      {getStatusIcon(pipeline.lastExecution.status)}
                      <Badge variant={getStatusBadgeVariant(pipeline.lastExecution.status)}>
                        {pipeline.lastExecution.status}
                      </Badge>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Never run</span>
                  )}
                </TableCell>
                <TableCell>
                  {pipeline.lastExecution ? (
                    <span className="text-sm text-muted-foreground">
                      {formatRelativeTime(pipeline.lastExecution.startedAt)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {pipeline.schedule ? (
                    <div className="text-sm">
                      <div className="font-medium text-foreground">
                        {formatCronExpression(pipeline.schedule)}
                      </div>
                      {pipeline.nextRunAt && (
                        <div className="text-xs text-muted-foreground">
                          {formatRelativeTime(pipeline.nextRunAt)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-sm">{pipeline.nodeCount}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full',
                        pipeline.healthStatus === 'healthy' && 'bg-green-500',
                        pipeline.healthStatus === 'warning' && 'bg-yellow-500',
                        pipeline.healthStatus === 'error' && 'bg-red-500'
                      )}
                    />
                    <span className="text-sm capitalize">{pipeline.healthStatus}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onRun(pipeline.id)}
                    >
                      <Clock className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEdit(pipeline.id)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onRun(pipeline.id)}>
                          <Clock className="h-4 w-4 mr-2" />
                          Run Now
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEdit(pipeline.id)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Pipeline
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onSchedule(pipeline.id)}>
                          <CalendarClock className="h-4 w-4 mr-2" />
                          {pipeline.schedule ? 'Edit Schedule' : 'Configure Schedule'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onDuplicate(pipeline.id)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDelete(pipeline.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
